import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PILLARS } from "@/constants/site";
import { PILLARS_IDS } from "@/constants/testIds";

const revealUp = {
  hidden: { y: 60, opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      delay: 0.08 * i,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const Pillars = () => {
  return (
    <section
      id="pillars"
      data-testid={PILLARS_IDS.root}
      className="section-rule bg-[#0A0A0A] py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        {/* Section head */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="md:col-span-7">
            <div className="num-tag mb-4">/ CHAPTER 02 · THE FOUR PILLARS</div>
            <motion.h2
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
            >
              Built four
              <br />
              <span className="stroke-fire">ways over.</span>
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-zinc-400 max-w-[42ch] text-base md:text-lg leading-relaxed">
              Everything TWGAS does routes through four pillars. Miss one and the
              athlete is unfinished. Nail all four and the work will always
              show.
            </p>
          </div>
        </div>

        {/* Bento */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {PILLARS.map((p, i) => (
            <motion.article
              key={p.key}
              data-testid={PILLARS_IDS.card(p.key)}
              variants={revealUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={i}
              className={`group relative border border-white/10 bg-[#111] overflow-hidden ${
                i % 3 === 0 ? "md:col-span-7" : "md:col-span-5"
              }`}
            >
              <div className="relative h-[220px] md:h-[280px] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 scale-[1.02] group-hover:scale-105 transition-[filter,transform] duration-[900ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="chip">
                    <span className="dot" />
                    {p.tag}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4 font-display text-white/80 text-[80px] leading-none">
                  {p.num}
                </div>
              </div>

              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display uppercase text-3xl md:text-4xl leading-none tracking-tight">
                    {p.title}
                  </h3>
                  <ArrowUpRight
                    size={22}
                    className="text-zinc-500 group-hover:text-[#FF4D00] group-hover:-translate-y-1 group-hover:translate-x-1 transition-[color,transform]"
                  />
                </div>
                <p className="mt-3 text-zinc-300 text-base md:text-lg leading-relaxed max-w-[54ch]">
                  {p.lead}
                </p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-sm text-zinc-400 flex gap-2 items-start"
                    >
                      <span className="mt-2 w-1 h-1 bg-[#FF4D00] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pillars;
